import { useI18n } from 'vue-i18n'
import { useFormat } from '@/lib/format'
import { useSaid, type SaidParams } from '@/lib/said'

/**
 * Notifications and badges in the user's language (review B46): the server
 * sends each as a code and its values; its French text shows only for the rows
 * written before, or a code the screens do not know yet.
 */
export function useNotices() {
  const { t, te } = useI18n()
  const f = useFormat()
  const say = useSaid()
  const place = (n: number) => (n === 1 ? t('gamification.place.first') : t('gamification.place.other', { n }))

  /** A badge's name, from its code. */
  const badgeLabel = (code: string, fallback = code) =>
    (te(`gamification.badge.${code}.label`, 'fr') ? t(`gamification.badge.${code}.label`) : fallback)
  /** A badge's rule, with the company's thresholds. */
  function badgeRule(code: string, params: SaidParams | undefined, fallback = '') {
    const p = { ...params }
    if (p.amount !== undefined && p.amount !== null) p.amount = f.money(String(p.amount), String(p.currency ?? ''))
    return say(`gamification.badge.${code}.rule`, p, fallback)
  }

  /** The values a notice is written with: names, places and badges as the reader says them. */
  function values(code: string, params: SaidParams): SaidParams {
    const p: SaidParams = { ...params }
    if ('who' in p) p.who = p.who ?? t('gamification.said.someone')
    if ('name' in p) p.name = p.name ?? t('gamification.said.station', { exten: p.exten ?? '' })
    if (typeof p.rank === 'number') p.place = place(p.rank)
    // An order's number is an identifier: never grouped like a quantity.
    if (p.order !== undefined && p.order !== null) p.order = String(p.order)
    if (typeof p.badge === 'string') {
      const { badge, order: _order, ...rule } = params
      p.rule = badgeRule(String(badge), rule)
      p.badge = badgeLabel(String(badge))
    }
    if (code === 'pause_long_named' || code === 'pause_over') p.pause = p.pause ?? ''
    return p
  }

  interface Notice { code?: string | null; params?: SaidParams | null; title: string; body: string | null }
  const title = (x: Notice) => (x.code ? say(`gamification.said.${x.code}.title`, values(x.code, x.params ?? {}), x.title) : x.title)
  const body = (x: Notice) => (x.code ? say(`gamification.said.${x.code}.body`, values(x.code, x.params ?? {}), x.body ?? '') || null : x.body)
  return { title, body, badgeLabel, badgeRule }
}
